import type { StrapiImageMetadata, StrapiImageSize } from "ui/StrapiImage";

/**
 * A pack groups several products in a single photo, so its picture is
 * panoramic. It cannot fit the square thumbnail that a single accessory uses.
 * We read the shape of the picture instead of a catalog flag, because the
 * constraint is the shape itself. A new pack needs no code change, and a model
 * that keeps one picture per accessory never meets this rule.
 */
export const WIDE_PICTURE_RATIO = 2;

/**
 * Every format of one picture holds the same ratio, so the first format that
 * carries its dimensions answers the question.
 */
const FORMATS_TO_READ: Array<StrapiImageSize> = ["large", "medium", "thumbnail"];

type PictureFormats = Partial<
  Record<StrapiImageSize, Partial<StrapiImageMetadata>>
>;

const hasDimensions = (format?: Partial<StrapiImageMetadata>) =>
  Number(format?.width) > 0 && Number(format?.height) > 0;

/**
 * Answers true when the picture is at least WIDE_PICTURE_RATIO times wider
 * than it is tall.
 *
 * `stripMetadata` gives an empty object for a format that Strapi did not
 * build, and Strapi builds no format larger than the source file. So this
 * function looks for dimensions, and it does not stop on the first key it
 * finds. A picture that carries no dimensions at all stays square.
 */
export const isWidePicture = (image?: PictureFormats | null) => {
  const format = FORMATS_TO_READ.map((size) => image?.[size]).find(
    hasDimensions,
  );

  return Number(format?.width) >= Number(format?.height) * WIDE_PICTURE_RATIO;
};
